import { getPresenceLabelForDay } from './equipePlanningUtils';

/** Ordre Lun → Dim (aligné grille présences). */
const WEEK_DAYS = [
  { value: 1, short: 'Lun' },
  { value: 2, short: 'Mar' },
  { value: 3, short: 'Mer' },
  { value: 4, short: 'Jeu' },
  { value: 5, short: 'Ven' },
  { value: 6, short: 'Sam' },
  { value: 0, short: 'Dim' },
];

function dayRangeLabel(from, to) {
  return from === to ? from : `${from}–${to}`;
}

function groupConsecutiveByLabel(entries) {
  const groups = [];
  let i = 0;
  while (i < entries.length) {
    const label = entries[i].label;
    let j = i;
    while (j + 1 < entries.length && entries[j + 1].label === label) {
      j += 1;
    }
    groups.push({
      from: entries[i].short,
      to: entries[j].short,
      label,
    });
    i = j + 1;
  }
  return groups;
}

function dominantLabel(entriesWithHours) {
  const counts = new Map();
  for (const e of entriesWithHours) {
    counts.set(e.label, (counts.get(e.label) || 0) + 1);
  }
  let best = null;
  let bestN = 0;
  for (const [label, n] of counts) {
    if (n > bestN) {
      best = label;
      bestN = n;
    }
  }
  return best;
}

/**
 * Résumé compact des horaires habituels (lecture seule, pas de logique métier nouvelle).
 * @returns {{ primary: string | null, exceptions: string[], empty: boolean }}
 */
export function formatCompactHabitualHours(horairesConfig) {
  const entries = WEEK_DAYS.map((d) => ({
    ...d,
    label: getPresenceLabelForDay(horairesConfig, d.value),
  }));

  const withHours = entries.filter((e) => e.label);
  if (!withHours.length) {
    return {
      primary: null,
      exceptions: [],
      empty: true,
    };
  }

  const groups = groupConsecutiveByLabel(entries);
  const filledGroups = groups.filter((g) => g.label);

  if (filledGroups.length === 1) {
    const g = filledGroups[0];
    const span =
      g.from === 'Lun' && g.to === 'Dim' ? 'Lun–Dim' : dayRangeLabel(g.from, g.to);
    return {
      primary: `${span} · ${g.label}`,
      exceptions: [],
      empty: false,
    };
  }

  const dominant = dominantLabel(withHours);
  const majority =
    withHours.filter((e) => e.label === dominant).length >= Math.ceil(withHours.length / 2);

  if (majority && dominant) {
    const exceptions = entries
      .filter((e) => e.label !== dominant)
      .map((e) => `${e.short} · ${e.label || 'Fermé'}`);

    if (exceptions.length === 0) {
      return { primary: `Lun–Dim · ${dominant}`, exceptions: [], empty: false };
    }

    return {
      primary: `Habituellement · ${dominant}`,
      exceptions,
      empty: false,
    };
  }

  const lines = filledGroups
    .slice(0, 3)
    .map((g) => `${dayRangeLabel(g.from, g.to)} · ${g.label}`);

  return {
    primary: lines.length === 1 ? lines[0] : null,
    exceptions: lines.length === 1 ? [] : lines,
    empty: false,
  };
}
